import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Navbar({ onBookingClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#om-os", label: "Om Os" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#2F3E30] shadow-md"
          : "bg-[#2F3E30] shadow-sm"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo-link">
            <Home className="w-6 h-6 text-white" />
            <span className="font-semibold text-xl md:text-2xl tracking-wide text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Salgsrenovering
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-white/80 hover:text-white transition-colors tracking-wide uppercase"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:+4561362199" 
              className="flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              +45 6136 2199
            </a>
            <Button 
              onClick={onBookingClick}
              className="bg-white text-[#2F3E30] hover:bg-white/90 rounded-none px-6"
              data-testid="nav-booking-btn"
            >
              Book Konsultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-border"
          >
            <div className="py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors uppercase tracking-wide"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="px-4 pt-4 border-t border-border">
                <Button 
                  onClick={() => {
                    onBookingClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none"
                  data-testid="mobile-booking-btn"
                >
                  Book Konsultation
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
}
